import React from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';

const COMMENTS_QUERY = gql`
  query Comments($type: String!, $id: ID!, $limit: Int) {
    sprint(id: $id) @include(if: $isSprint) { id comments(limit: $limit) { id comment created_at user { id allowed_name profile_picture student_avatar_url } author_role } }
    actionPlanItem(id: $id) @include(if: $isItem) { id comments(limit: $limit) { id comment created_at user { id allowed_name profile_picture student_avatar_url } author_role } }
  }`;

const ADD_COMMENT_TO_SPRINT = gql`
  mutation AddCommentToSprint($sprint_id: ID!, $comment: String!) {
    add_comment_to_sprint(sprint_id: $sprint_id, comment: $comment) { id comment created_at user { id allowed_name profile_picture student_avatar_url } author_role }
  }`;

const ADD_COMMENT_TO_ITEM = gql`
  mutation AddCommentToActionPlanItem($item_id: ID!, $comment: String!) {
    add_comment_to_action_plan_item(item_id: $item_id, comment: $comment) { id comment created_at user { id allowed_name profile_picture student_avatar_url } author_role }
  }`;

const MARK_READ = gql`
  mutation MarkRead($commentable_type: String!, $commentable_id: ID!) {
    mark_comments_read(commentable_type: $commentable_type, commentable_id: $commentable_id)
  }`;

const formatDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(); } catch (_) { return ''; }
};

const resolveAvatarSrc = (val) => {
  if (!val) return null;
  if (val.startsWith('http') || val.startsWith('/')) return val;
  return `https://s3-us-west-2.amazonaws.com/findbob-production/images/${val}`;
};

const CommentAvatar = ({ user: u }) => {
  const [failed, setFailed] = React.useState(false);
  const src = resolveAvatarSrc(u?.student_avatar_url || u?.profile_picture);
  const initial = (u?.allowed_name || u?.name || 'U').charAt(0).toUpperCase();

  if (src && !failed) {
    return <img alt={initial} src={src} onError={() => setFailed(true)} className="w-8 h-8 rounded-full border border-succession-border object-cover" />;
  }
  return (
    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-xs font-semibold border border-succession-border">
      {initial}
    </div>
  );
};

const CoachesCornerComments = ({ itemId, itemType, maxHeight = '400px', onCommentAdded, className = '' }) => {
  const isSprint = itemType === 'Sprint';
  const isItem = itemType === 'ActionPlanItem';

  const { data, loading, refetch } = useQuery(gql`
    query FetchComments($id: ID!, $isSprint: Boolean!, $isItem: Boolean!, $limit: Int) {
      sprint: sprint(id: $id) @include(if: $isSprint) { id comments(limit: $limit) { id comment created_at user { id allowed_name profile_picture student_avatar_url } author_role } }
      item: action_plan_item(id: $id) @include(if: $isItem) { id comments(limit: $limit) { id comment created_at user { id allowed_name profile_picture student_avatar_url } author_role } }
    }
  `, { variables: { id: itemId, isSprint, isItem, limit: 50 } });

  const [addSprintComment] = useMutation(ADD_COMMENT_TO_SPRINT);
  const [addItemComment] = useMutation(ADD_COMMENT_TO_ITEM);
  const [markRead] = useMutation(MARK_READ);

  React.useEffect(() => {
    markRead({ variables: { commentable_type: itemType, commentable_id: itemId } }).catch(() => {});
  }, [itemType, itemId, markRead]);

  const comments = (isSprint ? data?.sprint?.comments : data?.item?.comments) || [];
  const [text, setText] = React.useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const vars = isSprint ? { sprint_id: itemId, comment: text.trim() } : { item_id: itemId, comment: text.trim() };
      const mutate = isSprint ? addSprintComment : addItemComment;
      await mutate({ variables: vars });
      setText('');
      onCommentAdded && onCommentAdded();
      refetch();
    } catch (_) {}
  };

  return (
    <div className={`bg-succession-bg-primary border border-succession-border rounded-lg ${className}`}>
      <div className="p-4 max-h-[${maxHeight}] overflow-y-auto space-y-4">
        {loading && <div className="text-succession-text-secondary text-sm">Loading…</div>}
        {!loading && comments.length === 0 && (
          <div className="text-succession-text-secondary text-sm">No comments yet.</div>
        )}
        {comments.map((c) => (
          <div key={c.id} className="flex items-start gap-3">
            <CommentAvatar user={c.user} />
            <div className="flex-1">
              <div className="flex items-center gap-2 text-xs text-succession-text-secondary">
                <span className="font-medium text-succession-text-primary">{c.user?.allowed_name}</span>
                {c.author_role && <span className="px-2 py-0.5 rounded-full border border-succession-border">{c.author_role.charAt(0).toUpperCase() + c.author_role.slice(1)}</span>}
                <span>{formatDate(c.created_at)}</span>
              </div>
              <div className="mt-1 text-sm text-succession-text-primary bg-succession-bg-secondary border border-succession-border rounded-lg px-3 py-2">{c.comment}</div>
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={handleSubmit} className="border-t border-succession-border p-3 flex items-center gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a comment… (Ctrl+Enter to send)"
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { handleSubmit(e); } }}
          className="flex-1 px-3 py-2 rounded-md bg-succession-bg-primary border border-succession-border text-succession-text-primary focus:outline-none"
        />
        <button type="submit" className="px-3 py-2 text-sm rounded-md bg-succession-primary text-white hover:bg-succession-primary/90">Send</button>
      </form>
    </div>
  );
};

export default CoachesCornerComments;


